// ===========================================
// Custom SMS & WhatsApp Analytics Page
// ===========================================
// Display delivery analytics for a SMS/WhatsApp campaign

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box,
  Paper,
  Button,
  Stack,
  CircularProgress,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SentIcon,
  DoneAll as DeliveredIcon,
  Error as FailedIcon,
  SwapHoriz as FallbackIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import {
  useCustomSmsCampaign,
  useSmsCampaignStats,
  useSmsCampaignAnalyticsRecords,
} from '../../hooks/useCustomSms';

// ===========================================
// STAT CARD COMPONENT
// ===========================================

interface StatCardProps {
  title: string;
  value: number;
  total?: number;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'info' | 'warning' | 'error';
  percentage?: number;
}

function StatCard({ title, value, total, icon, color, percentage }: StatCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}.lighter`,
              color: `${color}.main`,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {value.toLocaleString()}
              {total !== undefined && (
                <Typography component="span" variant="body2" color="text.secondary">
                  {' / '}{total.toLocaleString()}
                </Typography>
              )}
            </Typography>
            {percentage !== undefined && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(percentage, 100)}
                  color={color}
                  sx={{ height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {percentage.toFixed(1)}%
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function CustomSmsAnalyticsPage() {
  const { schoolSlug } = useTenant();
  const { id } = useParams<{ id: string }>();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data: campaign, isLoading: isLoadingCampaign } = useCustomSmsCampaign(id || '');
  const { data: stats, isLoading: isLoadingStats, error: statsError } = useSmsCampaignStats(id || '');
  const { data: recordsData, isLoading: isLoadingRecords } = useSmsCampaignAnalyticsRecords(id || '', {
    page: page + 1,
    limit: rowsPerPage,
  });

  if (isLoadingCampaign || isLoadingStats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (statsError || !campaign) {
    return (
      <Box>
        <PageHeader
          title="Campaign Analytics"
          action={
            <Button startIcon={<BackIcon />} component={Link} to={`/${schoolSlug}/admin/custom-sms`}>
              Back
            </Button>
          }
        />
        <Alert severity="error">
          Failed to load analytics:{' '}
          {statsError instanceof Error ? statsError.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  const records = recordsData?.records || [];
  const totalRecords = recordsData?.total || 0;
  const totalRecipients = stats?.totalRecipients || campaign.totalRecipients;

  return (
    <Box>
      <PageHeader
        title="Campaign Analytics"
        subtitle={campaign.title}
        action={
          <Button startIcon={<BackIcon />} component={Link} to={`/${schoolSlug}/admin/custom-sms`}>
            Back to Campaigns
          </Button>
        }
      />

      {/* Campaign Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6">{campaign.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {campaign.body.length > 100 ? `${campaign.body.substring(0, 100)}…` : campaign.body}
            </Typography>
            {campaign.sentAt && (
              <Typography variant="body2" color="text.secondary">
                Sent: {new Date(campaign.sentAt).toLocaleString()}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {campaign.channel === 'SMS' || campaign.channel === 'BOTH' ? (
              <Chip icon={<SmsIcon />} label="SMS" size="small" color="primary" variant="outlined" />
            ) : null}
            {campaign.channel === 'WHATSAPP' || campaign.channel === 'BOTH' ? (
              <Chip
                icon={<WhatsAppIcon />}
                label="WhatsApp"
                size="small"
                color="success"
                variant="outlined"
              />
            ) : null}
            <Chip
              label={campaign.status}
              color={campaign.status === 'SENT' ? 'success' : 'default'}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard
          title="Total Recipients"
          value={totalRecipients}
          icon={<SentIcon />}
          color="primary"
        />
        <StatCard
          title="Sent"
          value={stats?.sentCount || 0}
          total={totalRecipients}
          icon={<SentIcon />}
          color="info"
          percentage={totalRecipients > 0 ? ((stats?.sentCount || 0) / totalRecipients) * 100 : 0}
        />
        <StatCard
          title="Delivered"
          value={stats?.deliveredCount || 0}
          total={stats?.sentCount || 0}
          icon={<DeliveredIcon />}
          color="success"
          percentage={stats?.sentCount ? ((stats.deliveredCount || 0) / stats.sentCount) * 100 : 0}
        />
        <StatCard
          title="Failed"
          value={stats?.failedCount || 0}
          icon={<FailedIcon />}
          color="error"
          percentage={totalRecipients > 0 ? ((stats?.failedCount || 0) / totalRecipients) * 100 : 0}
        />
        <StatCard
          title="SMS Sent"
          value={stats?.smsCount || 0}
          icon={<SmsIcon />}
          color="primary"
        />
        <StatCard
          title="WhatsApp Sent"
          value={stats?.whatsappCount || 0}
          icon={<WhatsAppIcon />}
          color="success"
        />
      </Box>

      {/* Fallback info */}
      {(stats?.fallbackCount || 0) > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FallbackIcon color="warning" />
            <Typography variant="body2">
              <strong>{stats?.fallbackCount}</strong> recipients were sent via SMS fallback (WhatsApp
              unavailable or failed).
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* Delivery Log */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">Delivery Log</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Recipient</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Fallback</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Delivered</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingRecords ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">No delivery records found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{record.recipientName}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {record.recipientPhone}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.recipientType}
                        size="small"
                        color={record.recipientType === 'PARENT' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {record.channelUsed === 'WHATSAPP' ? (
                        <Chip
                          icon={<WhatsAppIcon />}
                          label="WhatsApp"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<SmsIcon />}
                          label="SMS"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {record.fallbackUsed ? (
                        <Chip label="Yes" size="small" color="warning" variant="outlined" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {record.sentAt ? new Date(record.sentAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {record.deliveredAt ? new Date(record.deliveredAt).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {record.failed ? (
                        <Chip
                          label={record.failureReason ? `Failed: ${record.failureReason.substring(0, 30)}` : 'Failed'}
                          size="small"
                          color="error"
                        />
                      ) : record.delivered ? (
                        <Chip label="Delivered" size="small" color="success" />
                      ) : record.sent ? (
                        <Chip label="Sent" size="small" color="primary" />
                      ) : (
                        <Chip label="Pending" size="small" color="default" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>
    </Box>
  );
}
